import React, { useState, memo, useEffect } from 'react';
import { Button, Input, Space } from 'antd';
import { useSelector } from 'react-redux';
import { useAtom } from 'jotai';
import { supportedMetricsSelector } from '@src/store/common/selectors';
import MetricSelectorPopup from '@components/common/MetricSelectorPopup';
import useSupportedMetrics from '@hooks/metrics/useSupportedMetrics';
import useSupportedFlowMetrics from '@hooks/metrics/useSupportedFlowMetrics';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import { EditWrapper } from './styled';
import { useCurrentLimitation } from '@hooks/useCurrentLimitation';
import apptracker from '@src/utils/sensor/apptracker';
import { CONFIGURED_METRIC_GROUP_WIDGET } from '@src/utils/sensor/track';
import { currentKpiIdAtom } from '@src/atoms/panels';

function Edit({ selectedMetrics, onSave, onCancel, groupName }) {
  const supportedMetrics: Record<string, any> = useSelector(supportedMetricsSelector);
  const [currentMetrics, setCurrentMetrics] = useState(selectedMetrics);
  const [name, setName] = useState(groupName);
  const globalExcludeMetricsCategory = useGlobalExcludeMetricCategories([]);
  const limitation = useCurrentLimitation();
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);

  useEffect(() => {
    const filteredMetrics = selectedMetrics.filter(
      (metric) => supportedMetrics[metric] && supportedMetrics[metric].active,
    );
    setCurrentMetrics(filteredMetrics);
  }, [selectedMetrics, supportedMetrics]);

  const metrics = useSupportedMetrics({
    excludeCategories: globalExcludeMetricsCategory,
    limitation,
    allMetrics: supportedMetrics,
  });

  const flowData = useSupportedFlowMetrics({
    allMetrics: supportedMetrics,
  });

  const onApply = (updatedMetrics) => {
    setCurrentMetrics(updatedMetrics);
  };

  const handleSave = () => {
    onSave({ metrics: currentMetrics, groupName: name });
    apptracker(CONFIGURED_METRIC_GROUP_WIDGET, { metrics: currentMetrics, name });
  };

  const handleChange = (e) => {
    const value = e.target.value.trim();
    setName(value);
  };

  return (
    <EditWrapper>
      <div>Organize related metrics together</div>
      <Space direction="vertical">
        <Input
          addonBefore="Group Name"
          value={name}
          defaultValue="Metric Group"
          onChange={handleChange}
          maxLength={30}
          minLength={3}
          required
          style={{ width: 350 }}
        />
        <Space>
          <MetricSelectorPopup
            supportedMetrics={metrics}
            flowData={flowData}
            selectedMetrics={currentMetrics}
            lockedMetrics={[]}
            RenderBtn={Button}
            onApply={onApply}
            metricsCountLimit={4}
            config={limitation?.metrics?.config}
            currentKpiId={currentKpiId}
            onKpiChange={setCurrentKpiId}
          />
          <span style={{ fontSize: 12, color: 'rgb(140, 140, 140)' }}>
            {currentMetrics.length > 1
              ? `${currentMetrics.length} selected`
              : 'Min 2 metrics required'}
          </span>
        </Space>
      </Space>
      <Space>
        <Button
          type="primary"
          size="small"
          onClick={handleSave}
          disabled={currentMetrics.length < 2 || name === ''}
        >
          Save
        </Button>
        <Button
          type="default"
          size="small"
          onClick={onCancel}
          disabled={selectedMetrics.length < 2}
        >
          Cancel
        </Button>
      </Space>
    </EditWrapper>
  );
}

export default memo(Edit);
